import { formatDollars, formatPercent } from '@kwenta/sdk/utils'
import { type FC, memo } from 'react'
import { useTranslation } from 'react-i18next'

import Currency from 'components/Currency'
import { Body, NumericValue } from 'components/Text'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import MarginRatioBarChart from 'sections/futures/MarginRatioBar'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import {
	selectCrossMarginMarginInfo,
	selectMaintenanceMarginRatio,
} from 'state/futures/snxPerpsV3/selectors'
import { useAppSelector } from 'state/hooks'
import { providerIsCrossMargin } from 'utils/futures'

type Props = {
	maskAsSensitive: boolean
	availableMargin: string
	allocatedMargin: string
	upnl: string
}

const AccountHealth: FC<Props> = memo(
	({ maskAsSensitive, availableMargin, allocatedMargin, upnl }) => {
		const { t } = useTranslation()
		const { ratio, riskStatus } = useAppSelector(selectMaintenanceMarginRatio)
		const { requiredMaintenanceMargin } = useAppSelector(selectCrossMarginMarginInfo)
		const perpsProvider = useAppSelector(selectPerpsProvider)

		return (
			<FlexDivCol rowGap="5px">
				{providerIsCrossMargin(perpsProvider) ? (
					<>
						<FlexDivRowCentered justifyContent="space-between" columnGap="10px">
							<Body color="secondary">{t('dashboard.overview.account-health.margin-ratio')}:</Body>
							<NumericValue
								weight="semiBold"
								fontSize={15}
								color={
									riskStatus === 'high' ? 'negative' : riskStatus === 'low' ? 'positive' : 'preview'
								}
							>
								{formatPercent(ratio, { maxDecimals: 2 })}
							</NumericValue>
						</FlexDivRowCentered>
						{ratio.gt(0) && <MarginRatioBarChart ratio={ratio} risk={riskStatus} />}
						<FlexDivRowCentered justifyContent="space-between" columnGap="10px">
							<Body color="secondary">
								{t('dashboard.overview.account-health.maintenance-margin')}:
							</Body>
							<Body mono>{formatDollars(requiredMaintenanceMargin, { maskAsSensitive })}</Body>
						</FlexDivRowCentered>
						<FlexDivRowCentered justifyContent="space-between" columnGap="10px">
							<Body color="secondary">
								{t('dashboard.overview.account-health.available-balances')}:
							</Body>
							<Body mono weight="semiBold">
								{formatDollars(availableMargin, { maskAsSensitive, floor: true })}
							</Body>
						</FlexDivRowCentered>
					</>
				) : (
					<>
						<FlexDivRowCentered justifyContent="space-between" columnGap="10px">
							<Body color="secondary">
								{t('dashboard.overview.account-health.allocated-margin')}:
							</Body>
							<Body mono>{formatDollars(allocatedMargin, { maskAsSensitive })}</Body>
						</FlexDivRowCentered>
						<FlexDivRowCentered justifyContent="space-between" columnGap="10px">
							<Body color="secondary">
								{t('dashboard.overview.account-health.available-margin')}:
							</Body>
							<Body mono>{formatDollars(availableMargin, { maskAsSensitive, floor: true })}</Body>
						</FlexDivRowCentered>
						<FlexDivRowCentered justifyContent="space-between" columnGap="10px">
							<Body color="secondary">{t('dashboard.overview.account-health.upnl')}:</Body>
							<Currency.Price price={upnl} colored formatOptions={{ maskAsSensitive }} />
						</FlexDivRowCentered>
					</>
				)}
			</FlexDivCol>
		)
	}
)

export default AccountHealth
